import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { contentAPI } from '../api/content';

const EditContentMetricsModal = ({ isOpen, onClose, onSuccess, content }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isStream = content?.contentType === 'stream' || content?.contentType === 'vod' ||
    content?.platform === 'twitch' || content?.platform === 'kick';
  const isVideo = content?.platform === 'youtube' || content?.platform === 'tiktok' || content?.platform === 'instagram' || content?.platform === 'twitter';
  // YouTube streams have VOD engagement (likes/comments); Twitch VODs don't.
  const showVideoEngagement = isStream && content?.platform === 'youtube';

  // Format a Date/ISO string as "YYYY-MM-DD" for a native date input.
  const toDateInputValue = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (content) {
      setFormData({
        views: content.views || 0,
        likes: content.likes || 0,
        comments: content.comments || 0,
        shares: content.shares || 0,
        streamAvgCcv: content.streamAvgCcv || content.avgCcv || 0,
        streamPeakCcv: content.streamPeakCcv || content.peakCcv || 0,
        streamWatchHours: content.streamWatchHours || content.watchHours || 0,
        streamDuration: content.streamDuration || content.duration || 0,
        vodViews: content.vodViews || 0,
        impressions: content.impressions || 0,
        postDate: toDateInputValue(content.postDate),
      });
      setError('');
    }
  }, [content]);

  if (!isOpen || !content) return null;

  const handleChange = (field, value) => {
    const parsed = value === '' ? 0 : parseInt(value, 10);
    if (!isNaN(parsed)) {
      setFormData(prev => ({ ...prev, [field]: parsed }));
    }
  };

  const handleDateChange = (value) => {
    setFormData(prev => ({ ...prev, postDate: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const updates = {};

      // Only send fields that changed
      if (formData.views !== (content.views || 0)) updates.views = formData.views;
      if (formData.likes !== (content.likes || 0)) updates.likes = formData.likes;
      if (formData.comments !== (content.comments || 0)) updates.comments = formData.comments;
      if (formData.shares !== (content.shares || 0)) updates.shares = formData.shares;
      if (formData.streamAvgCcv !== (content.streamAvgCcv || content.avgCcv || 0)) {
        updates.streamAvgCcv = formData.streamAvgCcv;
        updates.avgCcv = formData.streamAvgCcv;
      }
      if (formData.streamPeakCcv !== (content.streamPeakCcv || content.peakCcv || 0)) {
        updates.streamPeakCcv = formData.streamPeakCcv;
        updates.peakCcv = formData.streamPeakCcv;
      }
      if (formData.streamWatchHours !== (content.streamWatchHours || content.watchHours || 0)) {
        updates.streamWatchHours = formData.streamWatchHours;
        updates.watchHours = formData.streamWatchHours;
      }
      if (formData.streamDuration !== (content.streamDuration || content.duration || 0)) {
        updates.streamDuration = formData.streamDuration;
        // Pass segment IDs so backend can update the game-specific segments
        if (content._gameSegmentIds?.length) {
          updates._gameSegmentIds = content._gameSegmentIds;
        }
      }
      if (formData.vodViews !== (content.vodViews || 0)) updates.vodViews = formData.vodViews;
      if (formData.impressions !== (content.impressions || 0)) updates.impressions = formData.impressions;

      // Post date — preserve the original time-of-day if we have one, so
      // we don't shift timestamps to 00:00 UTC just because the user picked
      // a different calendar day. If no prior postDate exists, use 12:00 UTC.
      const originalDateStr = toDateInputValue(content.postDate);
      if (formData.postDate !== originalDateStr) {
        if (formData.postDate) {
          const [y, m, d] = formData.postDate.split('-').map(Number);
          const base = content.postDate ? new Date(content.postDate) : null;
          const hh = base ? base.getUTCHours() : 12;
          const mm = base ? base.getUTCMinutes() : 0;
          const ss = base ? base.getUTCSeconds() : 0;
          updates.postDate = new Date(Date.UTC(y, m - 1, d, hh, mm, ss)).toISOString();
        } else {
          updates.postDate = null;
        }
      }

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setLoading(false);
        return;
      }

      await contentAPI.update(content.id, updates);

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating content metrics:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update metrics');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, field, hint) => (
    <div key={field}>
      <label className="block text-xs font-medium text-white mb-1">{label}</label>
      <input
        type="number"
        min="0"
        value={formData[field] ?? 0}
        onChange={(e) => handleChange(field, e.target.value)}
        className="w-full px-3 py-2 border border-white rounded-lg text-sm focus:ring-2 focus:ring-sc-pink/30 focus:border-sc-pink outline-none bg-white/5 text-white"
      />
      {hint && <p className="text-xs text-white mt-0.5">{hint}</p>}
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-1">Edit Metrics</h3>
        <p className="text-xs text-white mb-4 truncate" title={content.title}>
          {content.title || `${content.platform} ${content.contentType}`}
        </p>

        {error && (
          <div className="mb-3 p-2 bg-red-500/10 text-red-400 text-sm rounded">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Streams (Twitch + YouTube live VODs): VOD Views is the canonical
              view metric. The `views` column is legacy and kept in sync by
              the scraper, so it's not exposed here. */}
          {isStream && (
            <>
              {renderField('Duration (minutes)', 'streamDuration', 'Game segment duration in minutes')}
              <div className="grid grid-cols-2 gap-3">
                {renderField('Avg CCV', 'streamAvgCcv')}
                {renderField('Peak CCV', 'streamPeakCcv')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {renderField('Watch Hours', 'streamWatchHours')}
                {renderField('VOD Views', 'vodViews')}
              </div>
              {content?.platform === 'kick' && (
                <p className="text-xs text-white/80 bg-[#00e70a]/10 border border-[#00e70a]/40 rounded-md px-3 py-2">
                  Kick has no VOD API, so VOD views are entered by hand. Open the{' '}
                  <a href={content.contentUrl?.includes('/videos') ? content.contentUrl : `${(content.contentUrl || '').replace(/\/$/, '')}/videos`} target="_blank" rel="noopener noreferrer" className="underline text-white">videos tab on Kick</a>
                  {' '}and copy the view count. Kick deletes VODs after 7 days (30 for verified channels).
                </p>
              )}
              {showVideoEngagement && (
                <div className="grid grid-cols-2 gap-3">
                  {renderField('Likes', 'likes')}
                  {renderField('Comments', 'comments')}
                </div>
              )}
            </>
          )}

          {/* Videos (YouTube videos/shorts, TikTok, Instagram, Twitter) */}
          {!isStream && isVideo && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {renderField('Views', 'views')}
                {renderField('Likes', 'likes')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {renderField('Comments', 'comments')}
                {renderField('Shares', 'shares')}
              </div>
            </>
          )}

          {/* Fallback for non-stream, non-video content */}
          {!isStream && !isVideo && (
            renderField('Views', 'views')
          )}

          {renderField('Impressions', 'impressions', 'Leave 0 to auto-calculate from views')}

          {/* Post date — editable so users can correct imported content whose
              postDate was missing or wrong. Preserves original time-of-day. */}
          <div>
            <label className="block text-xs font-medium text-white mb-1">Post Date</label>
            <input
              type="date"
              value={formData.postDate ?? ''}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-white rounded-lg text-sm focus:ring-2 focus:ring-sc-pink/30 focus:border-sc-pink outline-none bg-white/5 text-white"
            />
            <p className="text-xs text-white mt-0.5">Controls the date shown on the campaign and in reports</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-white bg-white/5 hover:bg-white/10 border border-white rounded-lg text-sm font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-sc-pink hover:bg-sc-pink/90 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default EditContentMetricsModal;
